import { Component, effect, inject, input } from '@angular/core';
import { DatePipe } from '@angular/common';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { VisualBaseComponent } from '../visual-base.component';
import { QuarterlyGroup, QuarterlyGroupDetail } from '../../services/metric.service';
import { StateService } from '../../services/state.service';
import { Group } from '../../services/group.service';
import { VisualService } from '../../services/visual.service';

@Component({
  selector: 'app-rank-quarterly',
  standalone: true,
  imports: [DatePipe, NgxEchartsDirective],
  templateUrl: './rank-quarterly.component.html',
  styleUrl: './rank-quarterly.component.scss',
  providers: [provideEcharts()]
})
export class RankQuarterlyComponent extends VisualBaseComponent {
  metric = input<QuarterlyGroup | undefined>();
  
  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly date = inject(StateService).date;

  rankDisplay = 0;
  rankTotal = 0;

  override initOptions = { height: 180 }
  override baseOptions: EChartsOption = {
    aria: {
      enabled: true,
      decal: {
        show: false,
        decals: [
          {
            color: 'rgba(0, 0, 0, 0.2)',
            dashArrayX: [1, 0],
            dashArrayY: [2, 5],
            symbolSize: 1,
            rotation: 0.5235987755982988
          }
        ]
      }
    },
    grid: {
      left: 10,
      right: 10,
      top: 20, 
      bottom: 20  
    },
    tooltip: {
      trigger: 'axis',
      valueFormatter: value => VisualService.getValueDisplay(Number(value), this.metric()?.displayType)
    },
    xAxis: {
      type: 'category',
      show: true,
      data: []
    },
    yAxis: {
      type: 'value',
      show: false
    },
    series: [
      {
        name: 'Amount',
        type: 'bar',
        label: {
          show: true,
          position: 'top',
          formatter: (params) => { return VisualService.getValueDisplay(Number(params.value), this.metric()?.displayType); }
        },
        data: []
      },
      {
        name: 'Rank',
        type: 'custom',
        renderItem: () => { return null; },
        data: [],
      }
    ]
  }
  override loadingColor = '#ebebeb';

  constructor() {
    super();

    effect(() => {
      const group = this.peer()?.group;
      const date = this.date();
      const metric = this.metric();
      if (this.chartInit() && group && date && metric) {
        this.updateVisual(group, date, metric);
      }
    });
  }

  private updateVisual(group: Group, date: Date, metric: QuarterlyGroup) {
    if (group.groupType != 30) {
      const iids = this.filteredGroup()?.filter(i => i.isSelected).map(i => i.iid) ?? [];
      const institutions = metric.details?.filter(i => iids.includes(i.iid) || i.iid == this.institution().iid)
        .sort((a, b) => (metric.rankSort == 'L' ? a.value - b.value : b.value - a.value));
      institutions?.forEach((institution, index) => {
        institution.rank = index + 1;
      });
      this.updateChart(institutions ?? []);
    }
    else {
      this.metricService.getAggregateMetricRanks(this.institution().iid, metric.metricId, date, group.groupId)?.subscribe(institutions => {
        this.updateChart(institutions);
      });
    }
  }

  private updateChart(details: QuarterlyGroupDetail[]) {
    let data = [[], [], []];
    let minValue = -1; let maxValue = -1;

    const display = 25;
    const total = details.length;
    const index = details.findIndex((metric) => { return metric.iid == this.institution().iid })
    let start = 0; let end = total - 1;

    if (total > display) {
      if (index < ((display - 1) / 2)) {
        start = 0; end = display - 1;
      }
      else if (index > (total - ((display - 1) / 2))) {
        start = total - display; end = start + display;
      }
      else {
        start = index - ((display - 1) / 2); end = index + ((display - 1) / 2);
      }
    }

    this.rankTotal = total;
    this.rankDisplay = total >= display ? display : total;

    data = details.reduce((r: any, a: QuarterlyGroupDetail, i: number) => {
      if (i >= start && i <= end) {
        if (minValue == -1 || (VisualService.getValue(a.value, this.metric()?.displayType) ?? 0) < minValue) minValue = VisualService.getValue(a.value, this.metric()?.displayType) ?? 0;
        if (maxValue == -1 || (VisualService.getValue(a.value, this.metric()?.displayType) ?? 0) > maxValue) maxValue = VisualService.getValue(a.value, this.metric()?.displayType) ?? 0;
        if (a.iid == this.institution().iid) {
          r[0].push(this.institution().name);
          const value = {
            value: VisualService.getValue(a.value, this.metric()?.displayType), itemStyle: { color: this.colors[0], decal: { color: 'rgba(0, 0, 0, 0)' } }
          }
          r[1].push(value);
          r[2].push(a.rank);
        }
        else {
          r[0].push(a.label);
          r[1].push(VisualService.getValue(a.value, this.metric()?.displayType));
          r[2].push(a.rank);
        }
      }
      return r;
    }, [[], [], []]);

    const buffer = (maxValue == minValue) ? maxValue * .2 : (maxValue - minValue) * .2;
    minValue -= buffer; maxValue += buffer;

    this.updateOptions = {
      xAxis: {
        data: data[0],
        axisLabel: {
          formatter: (_: number, index: number) => {
            return data[2][index];
          }
        }
      },
      yAxis: {
        min: minValue,
        max: maxValue
      },
      series: [
        {
          itemStyle: {
            color: this.colors[1]
          },
          tooltip: {
            trigger: 'axis',
            valueFormatter: value => VisualService.getValueDisplay(Number(value), this.metric()?.displayType)
          },
          data: data[1]
        },
        {
          itemStyle: {
            color: this.colors[0]
          },
          tooltip: {
            trigger: 'axis',
            valueFormatter: value => value?.toString() ?? ''
          },
          data: data[2],
        }
      ]
    }
    this.echartsInstance?.hideLoading();
  }
}
